import { useState } from 'react';

import {
  AngleDown,
  AngleUp,
  Badge,
  ChakraBox,
  ChakraButton,
  ChakraCollapse,
  ChakraFlex,
  ChakraGridItem,
  ChakraSimpleGrid,
  ChakraSpacer,
  Divider,
  EquipUITheme,
  Text,
} from '@equip.health/ui';

import { PatientCoverage } from '~/lib/constants/patients.constants';
import { getPatientCoveragePriorityTextByValue } from '~/lib/util/patientCoverage.util';

import PatientCoverageDetails from './PatientCoverageDetails';

const { captionBold } = EquipUITheme.typography;

interface PatientCoverageListItemProps {
  coverage: PatientCoverage;
  coverages: PatientCoverage[];
  hasPatientDashboardPermission: boolean;
  onAdd: () => void;
  patientId: string;
  patientBasicDetails: Patient;
}
interface PatientCoverageListProps {
  coverages: PatientCoverage[];
  hasPatientDashboardPermission: boolean;
  onAdd: () => void;
  patientId: string;
  patientBasicDetails: Patient;
}
const commonBadgeStyles = {
  ...captionBold,
  badgeHeight: '18px',
  borderRadius: '6px',
  margin: '0 8px',
  padding: '4px 8px',
};

const PatientCoverageListItem = ({
  coverage,
  coverages,
  hasPatientDashboardPermission,
  onAdd,
  patientId,
  patientBasicDetails,
}: PatientCoverageListItemProps) => {
  const { coveragePriority, customPayorName, payor, isActive, subscriberId } =
    coverage;
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const handleToggle = (): void => setIsExpanded(!isExpanded);

  const TOP_LEVEL_ID = `patient-coverage-list-item__${coverage.coverageExternalId}`;
  const OTHER_INSURANCE_IDENTIFIER = 'other';

  const handleAdd = () => {
    onAdd();
    handleToggle();
  };

  return (
    <>
      <ChakraSimpleGrid
        borderColor="neutral.line"
        borderRadius="16px"
        borderStyle="solid"
        borderWidth="1px"
        gap={2}
        id={`${TOP_LEVEL_ID}__container`}
        marginY="5px"
        padding="23.5px 16px"
      >
        <ChakraGridItem>
          <ChakraFlex>
            <ChakraBox>
              <ChakraFlex alignItems="center">
                <Text id={`${TOP_LEVEL_ID}__name-text`} variant="bodyMedium">
                  {payor.toLowerCase() === OTHER_INSURANCE_IDENTIFIER
                    ? `${customPayorName || payor}`
                    : payor}
                </Text>
                {coveragePriority && (
                  <Badge
                    {...commonBadgeStyles}
                    badgeColor="secondary.5"
                    fontColor="secondary.50"
                    id={`${TOP_LEVEL_ID}__patient-coverage-priority`}
                  >
                    {getPatientCoveragePriorityTextByValue(coveragePriority)}
                  </Badge>
                )}
                <Badge
                  {...commonBadgeStyles}
                  badgeColor={
                    isActive ? 'tertiary.marigold.background' : 'neutral.line'
                  }
                  fontColor={
                    isActive ? 'tertiary.marigold.text' : 'neutral.secondary'
                  }
                  id={`${TOP_LEVEL_ID}__patient-coverage`}
                >
                  {isActive ? 'Active' : 'Inactive'}
                </Badge>
              </ChakraFlex>
              <Text
                color="neutral.secondary"
                id={`${TOP_LEVEL_ID}__subscriber-id`}
                variant="caption"
              >
                {subscriberId || '-'}
              </Text>
            </ChakraBox>
            <ChakraSpacer />
            <ChakraButton
              _hover={{ background: 'transparent' }}
              background="transparent"
              color="neutral.tertiary"
              fontWeight="700"
              id={`${TOP_LEVEL_ID}__expand`}
              onClick={handleToggle}
              rightIcon={
                isExpanded ? (
                  <AngleUp />
                ) : (
                  <AngleDown height="6px" width="10px" />
                )
              }
              size="xs"
            >
              {isExpanded ? 'Collapse' : 'Expand'}
            </ChakraButton>
          </ChakraFlex>
        </ChakraGridItem>
        <ChakraCollapse in={isExpanded}>
          <Divider id={`${TOP_LEVEL_ID}__coverage-details`} />
          <PatientCoverageDetails
            coverage={coverage}
            hasPatientDashboardPermission={hasPatientDashboardPermission}
            onAdd={handleAdd}
            patientBasicDetails={patientBasicDetails}
            patientCoverages={coverages}
            patientId={patientId}
          />
        </ChakraCollapse>
      </ChakraSimpleGrid>
    </>
  );
};

const PatientCoverageList = ({
  coverages,
  hasPatientDashboardPermission,
  onAdd,
  patientId,
  patientBasicDetails,
}: PatientCoverageListProps) => (
  <ChakraSimpleGrid gap={2}>
    {(coverages ?? []).map((coverage) => (
      <ChakraGridItem key={coverage.coverageExternalId}>
        <PatientCoverageListItem
          coverage={coverage}
          coverages={coverages}
          hasPatientDashboardPermission={hasPatientDashboardPermission}
          onAdd={onAdd}
          patientBasicDetails={patientBasicDetails}
          patientId={patientId}
        />
      </ChakraGridItem>
    ))}
  </ChakraSimpleGrid>
);

export default PatientCoverageList;
